
import { EVENTS_COLORS } from "@/config";
import moment from "moment";
import { uuid } from "vue-uuid";
import i18n from "@/i18n";


export function checkTimeValues(event: any) {
    const start = moment(event.startsAt)
    const end = moment(event.endsAt)
    return start.isBefore(end)
}

export function getNextDate(frequency: string, currentStartDate: Date, currentEndDate: Date) {
    switch (frequency) {
        case 'daily':
            currentStartDate.setDate(currentStartDate.getDate() + 1)
            currentEndDate.setDate(currentEndDate.getDate() + 1)
            break
        case 'monthly':
            currentStartDate.setMonth(currentStartDate.getMonth() + 1)
            currentEndDate.setMonth(currentEndDate.getMonth() + 1)
            break
        case 'weekly':
            currentStartDate.setDate(currentStartDate.getDate() + 7)
            currentEndDate.setDate(currentEndDate.getDate() + 7)
            break
        default:
            break
    }
}

// Formats event to be saved in the database
// the event attribute complies with the vue-cal format
export function formatEventForApi(event: any, language: string) {
    let color;
    if (event.color) {
        color = event.color || event.color.name;
    }
    if (event.class) {
        color = event.class;
    }
    // Sets the i18n language to match the user language
    i18n.global.locale = language || 'en';
    if (event.repeat.repeatable) {
        const events = []
        const timeSeriesId = uuid.v4()
        if (event.templatesElementsId) {
            const currentStartDate = new Date(event.start)
            const currentEndDate = new Date(event.end)
            for (let i = 0; i < event.repeat.count; i++) {
                events.push({
                    title: event.title,
                    color: EVENTS_COLORS[color],
                    description: event.content == null ? i18n.global.t('EVENT_MODAL.PLACEHOLDER') : event.content,
                    editable: event.completed ? false : true,
                    startsAt: currentStartDate.toISOString(),
                    endsAt: currentEndDate.toISOString(),
                    allDay: event.allDay,
                    completed: event.completed,
                    appointment: false,
                    rrule: { "timeSeriesId": timeSeriesId },
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    templatesElementsId: event.templatesElementsId,
                    pagesElementsId: event.pagesElementsId
                })
                getNextDate(event.repeat.frequency, currentStartDate, currentEndDate)
            }
            return events
        } else {
            const currentStartDate = new Date(event.start)
            const currentEndDate = new Date(event.end)
            for (let i = 0; i < event.repeat.count; i++) {
                events.push({
                    title: event.title,
                    color: EVENTS_COLORS[color],
                    description: event.content == null ? i18n.global.t('EVENT_MODAL.PLACEHOLDER') : event.content,
                    editable: event.completed ? false : true,
                    startsAt: new Date(currentStartDate).toISOString(),
                    endsAt: new Date(currentEndDate).toISOString(),
                    allDay: event.allDay,
                    completed: event.completed,
                    appointment: false,
                    rrule: { "timeSeriesId": timeSeriesId },
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    templatesElementsId: event.templatesElementsId,
                    pagesElementsId: event.pagesElementsId
                })
                getNextDate(event.repeat.frequency, currentStartDate, currentEndDate)
            }
            return events
        }
    } else {
        if (event.templatesElementsId) {
            return [{
                title: event.title,
                color: EVENTS_COLORS[color] || EVENTS_COLORS[event.class],
                description: event.content,
                editable: event.completed ? false : true,
                startsAt: event.start,
                endsAt: event.end,
                allDay: event.allDay,
                completed: event.completed,
                appointment: false,
                rrule: {},
                pagesElementsId: event.pagesElementsId,
                templatesElementsId: event.templatesElementsId,
                createdAt: new Date(),
                updatedAt: new Date()
            }];
        } else {
            return [{
                title: event.title,
                color: EVENTS_COLORS[color] || EVENTS_COLORS[event.class],
                description: event.content,
                editable: event.completed ? false : true,
                startsAt: event.start,
                endsAt: event.end,
                allDay: event.allDay,
                completed: event.completed,
                appointment: false,
                rrule: {},
                pagesElementsId: event.pagesElementsId,
                createdAt: new Date(),
                updatedAt: new Date()
            }];
        }
    }
}

export function addMissingKeys(event: any) {
    event.applyRange = {}
    event.toDelete = {}
    event.applyRange.range == 'current'
    event.toDelete.toDelete = false
    return event
}

export function formatEventForUpdateApi(event: any) {
    return {
        id: event.id,
        title: event.title,
        color: EVENTS_COLORS[event.color] || EVENTS_COLORS[event.class],
        description: event.content,
        editable: event.completed ? false : true,
        startsAt: event.start,
        endsAt: event.end,
        allDay: event.allDay,
        completed: event.completed,
        appointment: false,
        rrule: {}
    };
}

// If the event to update is part of a time series, its date wont be changed, just the hours and minutes
export function formatTimeSeriesEventForUpdateApi(oldEvent: any, data: any, timeSeriesUuid: string) {
    const startHours = moment(data.start).format('HH:mm').split(':')[0];
    const startMinutes = moment(data.start).format('HH:mm').split(':')[1];
    const endHours = moment(data.end).format('HH:mm').split(':')[0];
    const endMinutes = moment(data.end).format('HH:mm').split(':')[1];
    return {
        id: oldEvent.id,
        title: data.title,
        color: EVENTS_COLORS[data.color] || EVENTS_COLORS[data.class],
        description: data.content,
        editable: data.completed ? false : true,
        allDay: data.allDay,
        completed: data.completed,
        appointment: false,
        rrule: { "timeSeriesId": timeSeriesUuid },
        startsAt: new Date(new Date(oldEvent.startsAt).setHours(Number(startHours), Number(startMinutes), 0)).toISOString(),
        endsAt: new Date(new Date(oldEvent.endsAt).setHours(Number(endHours), Number(endMinutes), 0)).toISOString(),
    };
}

// Formats event to be used by vue-cal
// the event attribute complies with the format returned by the API
export function formatEventForVueCal(event: any) {
    // Helps handling events without end time from the old portal. It will assume the event is one hour long by adding one hour to the start time.
    let end = event.endsAt;
    if (!end) {
        end = new Date(event.startsAt);
        end.setTime(end.getTime() + 1 * 60 * 60 * 1000);
    }

    return {
        allDay: event.allDay,
        completed: event.completed,
        daysCount: 1,
        end: new Date(end),
        split: null,
        start: new Date(event.startsAt),
        title: event.title,
        class: event.color.name,
        content: event.description,
        id: event.id,
        pagesElementsId: event.pagesElementsId,
        templatesElementsId: event.templatesElementsId,
        uuid: uuid.v4(),
        resizable: !event.completed
    };
}

export function formatDate(date: any) {
    if (date) {
        return moment(date).format("YYYY-MM-DD HH:mm");
    }
}

export function fixTimeZone(event: any) {
    const tzOffset = new Date().getTimezoneOffset() * 60 * 1000;

    event.startsAt = new Date(
        new Date(event.startsAt).getTime() + tzOffset
    ).toISOString();

    event.endsAt = new Date(
        new Date(event.endsAt).getTime() + tzOffset
    ).toISOString();

    return event;
}